import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import "./index.css";

type Operator = "+" | "-" | "×" | "÷" | null;

export function App() {
	const [display, setDisplay] = useState("0");
	const [previousValue, setPreviousValue] = useState<number | null>(null);
	const [operator, setOperator] = useState<Operator>(null);
	const [waitingForOperand, setWaitingForOperand] = useState(false);

	const inputDigit = useCallback(
		(digit: string) => {
			if (waitingForOperand) {
				setDisplay(digit);
				setWaitingForOperand(false);
			} else {
				setDisplay(display === "0" ? digit : display + digit);
			}
		},
		[display, waitingForOperand],
	);

	const inputDecimal = useCallback(() => {
		if (waitingForOperand) {
			setDisplay("0.");
			setWaitingForOperand(false);
			return;
		}
		if (!display.includes(".")) {
			setDisplay(display + ".");
		}
	}, [display, waitingForOperand]);

	const clear = useCallback(() => {
		setDisplay("0");
		setPreviousValue(null);
		setOperator(null);
		setWaitingForOperand(false);
	}, []);

	const toggleSign = useCallback(() => {
		const value = parseFloat(display);
		if (value !== 0) {
			setDisplay(String(-value));
		}
	}, [display]);

	const inputPercent = useCallback(() => {
		const value = parseFloat(display);
		setDisplay(String(value / 100));
	}, [display]);

	const calculate = useCallback(
		(nextOperator: Operator) => {
			const inputValue = parseFloat(display);

			if (previousValue === null) {
				setPreviousValue(inputValue);
			} else if (operator) {
				let result: number;
				switch (operator) {
					case "+":
						result = previousValue + inputValue;
						break;
					case "-":
						result = previousValue - inputValue;
						break;
					case "×":
						result = previousValue * inputValue;
						break;
					case "÷":
						result = inputValue !== 0 ? previousValue / inputValue : 0;
						break;
					default:
						result = inputValue;
				}
				const formatted =
					result % 1 !== 0 ? parseFloat(result.toFixed(10)).toString() : result.toString();
				setDisplay(formatted);
				setPreviousValue(result);
			}

			setWaitingForOperand(true);
			setOperator(nextOperator);
		},
		[display, previousValue, operator],
	);

	const equals = useCallback(() => {
		calculate(null);
	}, [calculate]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key >= "0" && e.key <= "9") inputDigit(e.key);
			else if (e.key === ".") inputDecimal();
			else if (e.key === "+") calculate("+");
			else if (e.key === "-") calculate("-");
			else if (e.key === "*") calculate("×");
			else if (e.key === "/") {
				e.preventDefault();
				calculate("÷");
			} else if (e.key === "Enter" || e.key === "=") equals();
			else if (e.key === "Escape") clear();
			else if (e.key === "%") inputPercent();
			else if (e.key === "Backspace") {
				if (display.length > 1) {
					setDisplay(display.slice(0, -1));
				} else {
					setDisplay("0");
				}
			}
		},
		[inputDigit, inputDecimal, calculate, equals, clear, inputPercent, display],
	);

	const displayFontSize =
		display.length > 12
			? "text-2xl"
			: display.length > 8
				? "text-3xl"
				: "text-4xl";

	const btnBase =
		"h-16 text-lg font-medium rounded-xl transition-all active:scale-95";

	return (
		<div
			className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 p-4"
			onKeyDown={handleKeyDown}
			tabIndex={0}
		>
			<Card className="w-full max-w-xs shadow-2xl border-0 bg-slate-900 overflow-hidden">
				<CardContent className="p-4 space-y-3">
					{/* Display */}
					<div className="bg-slate-800 rounded-xl p-4 min-h-[80px] flex flex-col justify-end items-end">
						{operator && previousValue !== null && (
							<div className="text-slate-500 text-sm mb-1">
								{previousValue} {operator}
							</div>
						)}
						<div
							className={`${displayFontSize} font-semibold text-white tracking-tight truncate w-full text-right`}
						>
							{display}
						</div>
					</div>

					{/* Buttons */}
					<div className="grid grid-cols-4 gap-2">
						{/* Row 1 */}
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-600 hover:bg-slate-500 text-white`}
							onClick={clear}
						>
							AC
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-600 hover:bg-slate-500 text-white`}
							onClick={toggleSign}
						>
							+/−
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-600 hover:bg-slate-500 text-white`}
							onClick={inputPercent}
						>
							%
						</Button>
						<Button
							className={`${btnBase} bg-amber-500 hover:bg-amber-400 text-white ${operator === "÷" && waitingForOperand ? "ring-2 ring-white" : ""}`}
							onClick={() => calculate("÷")}
						>
							÷
						</Button>

						{/* Row 2 */}
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("7")}
						>
							7
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("8")}
						>
							8
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("9")}
						>
							9
						</Button>
						<Button
							className={`${btnBase} bg-amber-500 hover:bg-amber-400 text-white ${operator === "×" && waitingForOperand ? "ring-2 ring-white" : ""}`}
							onClick={() => calculate("×")}
						>
							×
						</Button>

						{/* Row 3 */}
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("4")}
						>
							4
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("5")}
						>
							5
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("6")}
						>
							6
						</Button>
						<Button
							className={`${btnBase} bg-amber-500 hover:bg-amber-400 text-white ${operator === "-" && waitingForOperand ? "ring-2 ring-white" : ""}`}
							onClick={() => calculate("-")}
						>
							−
						</Button>

						{/* Row 4 */}
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("1")}
						>
							1
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("2")}
						>
							2
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("3")}
						>
							3
						</Button>
						<Button
							className={`${btnBase} bg-amber-500 hover:bg-amber-400 text-white ${operator === "+" && waitingForOperand ? "ring-2 ring-white" : ""}`}
							onClick={() => calculate("+")}
						>
							+
						</Button>

						{/* Row 5 */}
						<Button
							variant="secondary"
							className={`${btnBase} col-span-2 bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={() => inputDigit("0")}
						>
							0
						</Button>
						<Button
							variant="secondary"
							className={`${btnBase} bg-slate-700 hover:bg-slate-600 text-white`}
							onClick={inputDecimal}
						>
							.
						</Button>
						<Button
							className={`${btnBase} bg-amber-500 hover:bg-amber-400 text-white`}
							onClick={equals}
						>
							=
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export default App;
